/**
 * Unified Cache Management System
 * 
 * This module provides a centralized cache management system that:
 * - Prevents memory leaks with automatic cleanup
 * - Provides consistent cache interface
 * - Handles cache invalidation automatically
 * - Supports different cache strategies
 */

// Cache configuration
const CACHE_CONFIG = {
  // Default TTL values (in milliseconds)
  DEFAULT_TTL: 5 * 60 * 1000, // 5 minutes
  USER_DATA_TTL: 30 * 60 * 1000, // 30 minutes
  USER_ROLE_TTL: 60 * 60 * 1000, // 60 minutes
  DASHBOARD_DATA_TTL: 5 * 60 * 1000, // 5 minutes
  ESSENTIAL_DATA_TTL: 10 * 60 * 1000, // 10 minutes
  MINIMAL_DATA_TTL: 2 * 60 * 1000, // 2 minutes
  MAINTENANCE_DATA_TTL: 7 * 60 * 1000, // 7 minutes
  PAYMENTS_DATA_TTL: 10 * 60 * 1000, // 10 minutes
  DUE_PAYMENTS_DATA_TTL: 5 * 60 * 1000, // 5 minutes
  PROPERTIES_TTL: 15 * 60 * 1000, // 15 minutes
  TENANTS_TTL: 15 * 60 * 1000, // 15 minutes
  
  // Cleanup interval
  CLEANUP_INTERVAL: 10 * 60 * 1000, // 10 minutes
  
  // Maximum cache size (in MB)
  MAX_CACHE_SIZE: 50, // 50MB
};

// Cache key prefixes for organization
const CACHE_PREFIXES = {
  USER: 'cache_user',
  DASHBOARD: 'cache_dashboard',
  FINANCE: 'cache_finance',
  PROPERTIES: 'cache_properties',
  TENANTS: 'cache_tenants',
  PAYMENTS: 'cache_payments',
  MAINTENANCE: 'cache_maintenance',
  SYSTEM: 'cache_system'
};

/**
 * Unified Cache Manager
 */
class CacheManager {
  constructor() {
    this.cleanupInterval = null;
    this.startCleanupInterval();
  }

  /**
   * Set cache item with automatic TTL
   */
  set(key, data, ttl = CACHE_CONFIG.DEFAULT_TTL, source = 'manual') {
    try {
      const item = {
        data,
        timestamp: Date.now(),
        ttl,
        size: this.calculateSize(data),
        source, // 'manual', 'realtime', 'background'
        sourceTimestamp: Date.now()
      };

      localStorage.setItem(key, JSON.stringify(item));

      // Check cache size and cleanup if needed
      this.checkCacheSize();

      return true;
    } catch (error) {
      console.warn('Cache set failed:', error);
      return false;
    }
  }

  /**
   * Get cache item with automatic expiration check
   */
  get(key) {
    try {
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return null;

      const item = JSON.parse(itemStr);
      if (!item || !item.timestamp) {
        this.remove(key);
        return null;
      }

      const isExpired = Date.now() - item.timestamp > item.ttl;
      if (isExpired) {
        this.remove(key);
        return null;
      }

      return item.data;
    } catch (error) {
      console.warn('Cache get failed:', error);
      this.remove(key);
      return null;
    }
  }

  /**
   * Get cache item with metadata (source, timestamp, etc.)
   */
  getWithMetadata(key) {
    try {
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return null;

      const item = JSON.parse(itemStr);
      if (!item || !item.timestamp) {
        this.remove(key);
        return null;
      }

      const isExpired = Date.now() - item.timestamp > item.ttl;
      if (isExpired) {
        this.remove(key);
        return null;
      }

      return {
        data: item.data,
        metadata: {
          timestamp: item.timestamp,
          ttl: item.ttl,
          source: item.source,
          sourceTimestamp: item.sourceTimestamp,
          age: Date.now() - item.timestamp,
          isExpired: false
        }
      };
    } catch (error) {
      console.warn('Cache getWithMetadata failed:', error);
      this.remove(key);
      return null;
    }
  }

  /**
   * Check if cached data is fresh enough to skip background refresh
   */
  isFresh(key, maxAge = 5 * 60 * 1000) { // 5 minutes default
    try {
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return false;

      const item = JSON.parse(itemStr);
      if (!item || !item.timestamp) return false;

      const age = Date.now() - item.timestamp;
      return age < maxAge;
    } catch (error) {
      console.warn('Cache isFresh check failed:', error);
      return false;
    }
  }

  /**
   * Check if data is from real-time source
   */
  isRealtime(key) {
    try {
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return false;

      const item = JSON.parse(itemStr);
      return item?.source === 'realtime';
    } catch (error) {
      console.warn('Cache isRealtime check failed:', error);
      return false;
    }
  }

  /**
   * Get cache source information
   */
  getSource(key) {
    try {
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return null;

      const item = JSON.parse(itemStr);
      return {
        source: item?.source || 'unknown',
        sourceTimestamp: item?.sourceTimestamp || item?.timestamp || null,
        age: item ? Date.now() - item.timestamp : null
      };
    } catch (error) {
      console.warn('Cache getSource failed:', error);
      return null;
    }
  }

  /**
   * Remove specific cache item
   */
  remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn('Cache remove failed:', error);
      return false;
    }
  }

  /**
   * Clear all cache items
   */
  clear() {
    try {
      const keysToRemove = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('cache_')) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      return true;
    } catch (error) {
      console.warn('Cache clear failed:', error);
      return false;
    }
  }

  /**
   * Clear cache by prefix
   */
  clearByPrefix(prefix) {
    try {
      const keysToRemove = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      return keysToRemove.length;
    } catch (error) {
      console.warn('Cache clear by prefix failed:', error);
      return 0;
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    try {
      let totalSize = 0;
      let itemCount = 0;
      let expiredCount = 0;
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('cache_')) {
          itemCount++;
          
          try {
            const item = JSON.parse(localStorage.getItem(key));
            if (item && item.size) {
              totalSize += item.size;
            }
            
            if (item && item.timestamp && (Date.now() - item.timestamp > item.ttl)) {
              expiredCount++;
            }
          } catch (error) {
            // Item is corrupted, count as expired
            expiredCount++;
          }
        }
      }
      
      return {
        totalSize: totalSize / (1024 * 1024), // Convert to MB
        itemCount,
        expiredCount,
        maxSize: CACHE_CONFIG.MAX_CACHE_SIZE
      };
    } catch (error) {
      console.warn('Cache stats failed:', error);
      return null;
    }
  }

  /**
   * Calculate approximate size of data in bytes
   */
  calculateSize(data) {
    try {
      const str = JSON.stringify(data);
      return new Blob([str]).size;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Check cache size and cleanup if needed
   */
  checkCacheSize() {
    const stats = this.getStats();
    if (stats && stats.totalSize > CACHE_CONFIG.MAX_CACHE_SIZE) {
      this.cleanup();
    }
  }

  /**
   * Cleanup expired and old cache items
   */
  cleanup() {
    try {
      const now = Date.now();
      const keysToRemove = [];
      const items = [];
      
      // Collect all cache items
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('cache_')) {
          try {
            const item = JSON.parse(localStorage.getItem(key));
            if (item && item.timestamp) {
              items.push({ key, ...item });
            } else {
              keysToRemove.push(key); // Corrupted item
            }
          } catch (error) {
            keysToRemove.push(key); // Corrupted item
          }
        }
      }
      
      // Remove expired items
      items.forEach(({ key, timestamp, ttl }) => {
        if (now - timestamp > ttl) {
          keysToRemove.push(key);
        }
      });
      
      // If still over limit, remove oldest items
      if (keysToRemove.length === 0) {
        const stats = this.getStats();
        if (stats && stats.totalSize > CACHE_CONFIG.MAX_CACHE_SIZE) {
          // Sort by timestamp and remove oldest
          items.sort((a, b) => a.timestamp - b.timestamp);
          const itemsToRemove = Math.ceil(items.length * 0.2); // Remove 20% oldest
          items.slice(0, itemsToRemove).forEach(item => {
            keysToRemove.push(item.key);
          });
        }
      }
      
      // Remove items
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      if (keysToRemove.length > 0) {
        console.log(`Cache cleanup: removed ${keysToRemove.length} items`);
      }
      
      return keysToRemove.length;
    } catch (error) {
      console.warn('Cache cleanup failed:', error);
      return 0;
    }
  }

  /**
   * Start automatic cleanup interval
   */
  startCleanupInterval() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, CACHE_CONFIG.CLEANUP_INTERVAL);
  }

  /**
   * Stop automatic cleanup interval
   */
  stopCleanupInterval() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Generate cache key with prefix
   */
  generateKey(prefix, identifier) {
    return `${prefix}_${identifier}`;
  }
}

// Create singleton instance
const cacheManager = new CacheManager();

// Export cache manager and utilities
export default cacheManager;

// Export cache keys for consistency
export const CACHE_KEYS = {
  // User-related cache keys
  USER_DATA: (uid) => cacheManager.generateKey(CACHE_PREFIXES.USER, `data_${uid}`),
  USER_ROLE: (uid) => cacheManager.generateKey(CACHE_PREFIXES.USER, `role_${uid}`),
  
  // Dashboard-related cache keys
  DASHBOARD_DATA: (uid) => cacheManager.generateKey(CACHE_PREFIXES.DASHBOARD, `data_${uid}`),
  DASHBOARD_ESSENTIAL: (uid) => cacheManager.generateKey(CACHE_PREFIXES.DASHBOARD, `essential_${uid}`),
  DASHBOARD_MINIMAL: (uid) => cacheManager.generateKey(CACHE_PREFIXES.DASHBOARD, `minimal_${uid}`),
  DASHBOARD_MAINTENANCE: (uid) => cacheManager.generateKey(CACHE_PREFIXES.DASHBOARD, `maintenance_${uid}`),
  DASHBOARD_PAYMENTS: (uid) => cacheManager.generateKey(CACHE_PREFIXES.DASHBOARD, `payments_${uid}`),
  DASHBOARD_DUE_PAYMENTS: (uid) => cacheManager.generateKey(CACHE_PREFIXES.DASHBOARD, `due_payments_${uid}`),
  
  // Finance-related cache keys
  FINANCE_DATA: (uid) => cacheManager.generateKey(CACHE_PREFIXES.FINANCE, `data_${uid}`),
  FINANCE_PAYMENTS: (uid) => cacheManager.generateKey(CACHE_PREFIXES.FINANCE, `payments_${uid}`),
  FINANCE_PROPERTIES: (uid) => cacheManager.generateKey(CACHE_PREFIXES.FINANCE, `properties_${uid}`),
  FINANCE_TENANTS: (uid) => cacheManager.generateKey(CACHE_PREFIXES.FINANCE, `tenants_${uid}`),
  
  // Properties-related cache keys
  PROPERTIES_ALL: (uid) => cacheManager.generateKey(CACHE_PREFIXES.PROPERTIES, `all_${uid}`),
  PROPERTIES_DETAILS: (propertyId) => cacheManager.generateKey(CACHE_PREFIXES.PROPERTIES, `details_${propertyId}`),
  
  // Tenants-related cache keys
  TENANTS_ALL: (uid) => cacheManager.generateKey(CACHE_PREFIXES.TENANTS, `all_${uid}`),
  TENANTS_DETAILS: (tenantId) => cacheManager.generateKey(CACHE_PREFIXES.TENANTS, `details_${tenantId}`),
  
  // Payments-related cache keys
  PAYMENTS_ALL: (uid) => cacheManager.generateKey(CACHE_PREFIXES.PAYMENTS, `all_${uid}`),
  PAYMENTS_DUE: (uid) => cacheManager.generateKey(CACHE_PREFIXES.PAYMENTS, `due_${uid}`),
  
  // Maintenance-related cache keys
  MAINTENANCE_ALL: (uid) => cacheManager.generateKey(CACHE_PREFIXES.MAINTENANCE, `all_${uid}`),
  MAINTENANCE_DETAILS: (maintenanceId) => cacheManager.generateKey(CACHE_PREFIXES.MAINTENANCE, `details_${maintenanceId}`),
  
  // System-related cache keys
  SYSTEM_SETTINGS: 'system_settings',
  SYSTEM_FEATURES: 'system_features'
};

// Export TTL constants
export const CACHE_TTL = CACHE_CONFIG;

// Export prefixes for advanced usage
export const CACHE_PREFIXES_EXPORT = CACHE_PREFIXES;

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    cacheManager.stopCleanupInterval();
  });
} 